<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'ContractServiceFees',
    path: '/contract-service-fees',
    component: 'ContractServiceFees',
    title: () => gettext('Contract service fees'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractServiceFees',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const dsParams = ref({
    skip_archived: true,
  });
  const ds = ref(null);
  const gridName = ref('contractServiceFees');
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'contract_revision',
      name: $fn.value.pgettext('Contract', 'Contract number'),
      sortPath: 'contract_revision.contract_number',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_revision.contract_number',
              source: 'row',
            },
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            skipExport: true,
            params: {
              id: 'contract_id',
            },
          },
        ],
      },
    },
    {
      id: 'contract_type',
      field: 'contract_revision',
      name: $fn.value.pgettext('Contracts', 'Type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_type',
              source: 'value',
            },
          },
          {
            name: 'gettext',
            context: 'ContractTypes',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapField: 'contract_revision.contract_type',
            variantMap: {
              rental: 'contract-type--rental',
              vendor: 'contract-type--vendor',
              rental_agreement: 'contract-type--rental_agreement',
              service: 'contract-type--service',
              parking: 'contract-type--parking',
              unknown: 'contract-type--unknown',
            },
          },
        ],
      },
    },
    {
      id: 'contract_status',
      field: 'contract_revision',
      name: $fn.value.pgettext('Contracts', 'Status'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'status',
              source: 'value',
            },
          },
          {
            name: 'gettext',
            context: 'ContractStatus',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapField: 'contract_revision.status',
            variantMap: {
              draft: 'light',
              approved: 'success',
              signed: 'success',
              active: 'primary',
              ended: 'warning',
              terminated: 'warning',
              rescinded: 'danger',
              archived: 'light',
            },
          },
        ],
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('ContractServiceFees', 'Rental'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.name }}</strong>\n\
    {{~#if $value.house_number }} | {{ $value.house_number }}{{/if }}\n\
    {{~#if $value.area }} ({{$value.area}}{{$value.unit_of_area}}){{/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental_id',
            },
          },
        ],
      },
    },
    {
      field: 'company',
      name: $fn.value.pgettext('Contract', 'Company'),
      sortPath: 'company.name',
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'company.id',
            },
          },
        ],
      },
    },
    {
      field: 'counterparty',
      name: $fn.value.pgettext('Contract', 'Counterparty'),
      sortPath: 'counterparty.name',
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'counterparty.id',
            },
          },
        ],
      },
    },
    {
      field: 'valid_from',
      name: $fn.value.pgettext('Contracts', 'Valid From'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'valid_to',
      name: $fn.value.pgettext('Contracts', 'Valid To'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'next_indexation_date',
      name: $fn.value.pgettext('Contracts', 'Next Indexation Date'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'name',
      name: $fn.value.pgettext('ContractService', 'Name'),
      type: 'command',
    },
    {
      field: 'amount',
      name: $fn.value.pgettext('ContractService', 'Amount'),
      type: 'command',
      align: 'right',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if amount }}\n\
    {{ number amount }} {{ currency }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'billable_amount',
      name: $fn.value.pgettext('ContractService', 'Total'),
      type: 'command',
      align: 'right',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if amount }}\n\
    {{ number billable_amount }} {{ currency }}\n\
    {{~#if (neq currency billing_currency) }}\n\
      (→ {{ billing_currency }})\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'clicksink',
          },
        ],
      },
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('ContractServiceFee', 'Unit'),
    },
    {
      field: 'quantity',
      name: $fn.value.pgettext('ContractService', 'Quantity'),
      type: 'command',
      align: 'right',
      formatter: {
        name: 'template',
        engine: 'hb',
        format:
          '<span>\n\
  {{~#if quantity }}\n\
    {{ number quantity }} {{ unit_of_quantity }}\n\
  {{~/if }}\n\
</span>\n\
',
      },
    },
    {
      field: 'inflation_indexed',
      name: $fn.value.pgettext('ContractService', 'Inflation indexed'),
      formatter: 'checkbox',
    },
    {
      field: 'transaction_notes',
      name: $fn.value.pgettext('ContractService', 'Invoice notes'),
      formatter: {
        name: 'join',
        separator: ', ',
      },
    },
    {
      field: 'billing_category',
      name: $fn.value.pgettext('ContractService', 'Billing category'),
    },
    {
      field: 'invoice_group_id',
      name: $fn.value.pgettext('ContractService', 'Invoice group'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('ContractService', 'Description'),
    },
    {
      field: 'intermediated_service',
      name: $fn.value.pgettext('ContractService', 'Intermediated service'),
      formatter: 'checkbox',
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    gridName: gridName.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('integro_api/list_contract_service_fee', {
      parameters: () => ({ ...dsParams.value, date: $fn.value.today() }),
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'ContractServiceFees.dsParams' },
      },
      { name: 'ds' },
      { name: 'gridName' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      dsParams,
      ds,
      gridName,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Contract service fees'),
      icon: $config.value.contract.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_3fs5prhbw_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_ndkck9vdv_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_arc64dh3j_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractService',
      props: {
        modal: true,
        oid: $event.data.data.contract_service_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_cv8107x6a_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'name';
  };
  const act_2248226175642056_showModal_u6ay3jlxa_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractServiceFee',
      props: {
        modal: true,
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_k7muiuj88_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'billable_amount';
  };
  const act_7172922356531116_reloadGrid_wyf1xfz9u_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_7172922356531116_close_g0kyoqhhk_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_7172922356531116_showModal_2hdmec476_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BulkContractServiceFeeEditor',
      props: {
        items: selectedItems.value,
      },
    };
  };
  const act_5246287230194476_reloadGrid_tiy81lt41_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_5246287230194476_request_omzcn43gu_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_delete_contract_service_fee',
      data: {
        items: _.map(selectedItems.value, (p) => _.pick(p, ['id', 'etag'])),
      },
    };
  };
  const act_6939677778299611_reloadGrid_6yamc1rqc_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_6939677778299611_close_6r20z3a88_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_6939677778299611_showModal_cogabsz27_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BulkIndexContractServiceFee',
      props: {
        items: selectedItems.value,
      },
    };
  };

  const act_7315092382398562_closeModal_3fs5prhbw =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_3fs5prhbw_getActionArgs,
    });
  const inst_evh_7315092382398562_close_2bgd5g7o8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_3fs5prhbw,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_2bgd5g7o8],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_84acev4cx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_yo5qo2zmq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_84acev4cx,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_yo5qo2zmq],
    event: 'reload',
  });
  const act_2248226175642056_setData_ndkck9vdv = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_ndkck9vdv_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_ic3ojtn6n =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_ndkck9vdv,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_ic3ojtn6n],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_arc64dh3j =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_arc64dh3j_getActionArgs,
    });
  const inst_evh_2248226175642056_command_cv8107x6a =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_arc64dh3j,
      event: 'command',
      when: inst_evh_2248226175642056_command_cv8107x6a_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_showModal_u6ay3jlxa =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_u6ay3jlxa_getActionArgs,
    });
  const inst_evh_2248226175642056_command_k7muiuj88 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_u6ay3jlxa,
      event: 'command',
      when: inst_evh_2248226175642056_command_k7muiuj88_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_cv8107x6a,
      inst_evh_2248226175642056_command_k7muiuj88,
    ],
    event: 'command',
  });
  const act_7172922356531116_reloadGrid_wyf1xfz9u =
    new actionTypes.ReloadGridAction({
      actionArgs: act_7172922356531116_reloadGrid_wyf1xfz9u_getActionArgs,
      displayName: 'reload',
    });
  const inst_evh_7172922356531116_close_g0kyoqhhk =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_reloadGrid_wyf1xfz9u,
      event: 'close',
      when: inst_evh_7172922356531116_close_g0kyoqhhk_getWhen,
      displayName: 'reload',
    });
  const act_7172922356531116_showModal_2hdmec476 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7172922356531116_showModal_2hdmec476_getActionArgs,
      events: [inst_evh_7172922356531116_close_g0kyoqhhk],
    });
  const inst_evh_7172922356531116_click_0xeppj7dr =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_showModal_2hdmec476,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_7172922356531116_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7172922356531116_click_0xeppj7dr],
    event: 'click',
  });
  const act_5246287230194476_reloadGrid_tiy81lt41 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_5246287230194476_reloadGrid_tiy81lt41_getActionArgs,
      displayName: 'reload',
    });
  const inst_evh_5246287230194476_success_07vgsxnjr =
    new actionTypes.EventHandlerImpl({
      action: act_5246287230194476_reloadGrid_tiy81lt41,
      event: 'success',
      displayName: 'reload',
    });
  const act_5246287230194476_request_omzcn43gu = new actionTypes.RequestAction({
    actionArgs: act_5246287230194476_request_omzcn43gu_getActionArgs,
    displayName: 'bulkDelete',
    events: [inst_evh_5246287230194476_success_07vgsxnjr],
  });
  const inst_evh_5246287230194476_click_u88u6gxuh =
    new actionTypes.EventHandlerImpl({
      action: act_5246287230194476_request_omzcn43gu,
      event: 'click',
      displayName: 'bulkDelete',
    });
  const inst_evh_5246287230194476_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5246287230194476_click_u88u6gxuh],
    event: 'click',
  });
  const act_6939677778299611_reloadGrid_6yamc1rqc =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6939677778299611_reloadGrid_6yamc1rqc_getActionArgs,
      displayName: 'reload',
    });
  const inst_evh_6939677778299611_close_6r20z3a88 =
    new actionTypes.EventHandlerImpl({
      action: act_6939677778299611_reloadGrid_6yamc1rqc,
      event: 'close',
      when: inst_evh_6939677778299611_close_6r20z3a88_getWhen,
      displayName: 'reload',
    });
  const act_6939677778299611_showModal_cogabsz27 =
    new actionTypes.ShowModalAction({
      actionArgs: act_6939677778299611_showModal_cogabsz27_getActionArgs,
      events: [inst_evh_6939677778299611_close_6r20z3a88],
    });
  const inst_evh_6939677778299611_click_4098mfzh6 =
    new actionTypes.EventHandlerImpl({
      action: act_6939677778299611_showModal_cogabsz27,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_6939677778299611_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6939677778299611_click_4098mfzh6],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_2bgd5g7o8,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_yo5qo2zmq,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_ic3ojtn6n,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_cv8107x6a,
    inst_evh_2248226175642056_command_k7muiuj88,
    inst_evh_2248226175642056_command,
    inst_evh_7172922356531116_close_g0kyoqhhk,
    reload: inst_evh_7172922356531116_close_g0kyoqhhk,
    inst_evh_7172922356531116_click_0xeppj7dr,
    inst_evh_7172922356531116_click,
    inst_evh_5246287230194476_success_07vgsxnjr,
    inst_evh_5246287230194476_click_u88u6gxuh,
    bulkDelete: inst_evh_5246287230194476_click_u88u6gxuh,
    inst_evh_5246287230194476_click,
    inst_evh_6939677778299611_close_6r20z3a88,
    inst_evh_6939677778299611_click_4098mfzh6,
    inst_evh_6939677778299611_click,
  };

  const evh_7315092382398562_close_2bgd5g7o8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_2bgd5g7o8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_yo5qo2zmq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_yo5qo2zmq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_ic3ojtn6n = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_ic3ojtn6n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_cv8107x6a = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_cv8107x6a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_k7muiuj88 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_k7muiuj88.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_close_g0kyoqhhk = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_close_g0kyoqhhk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click_0xeppj7dr = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click_0xeppj7dr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_success_07vgsxnjr = async (event, scope) => {
    return await actions.inst_evh_5246287230194476_success_07vgsxnjr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_click_u88u6gxuh = async (event, scope) => {
    return await actions.inst_evh_5246287230194476_click_u88u6gxuh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_click = async (event, scope) => {
    return await actions.inst_evh_5246287230194476_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6939677778299611_close_6r20z3a88 = async (event, scope) => {
    return await actions.inst_evh_6939677778299611_close_6r20z3a88.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6939677778299611_click_4098mfzh6 = async (event, scope) => {
    return await actions.inst_evh_6939677778299611_click_4098mfzh6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6939677778299611_click = async (event, scope) => {
    return await actions.inst_evh_6939677778299611_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractServiceFees"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      :name="gridName"
      :headerTitle="pgettext('ContractServiceFees', 'Contract service fees')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'contract-service-fees')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <div>
            <ItpFormCheckbox
              name="skip_archived"
              :label="pgettext('ContractServiceFees', 'Skip archived items')"
              inline
              v-model="dsParams.skip_archived"
            >
            </ItpFormCheckbox>
          </div>
          <div class="border-start ps-3 d-flex gap-2">
            <ItpButton
              :disabled="!selectedItems || !selectedItems.length"
              :icon="$config.contract.createContractIcon"
              :text="pgettext('ContractServiceFees', 'Bulk edit...')"
              size="sm"
              variant="light"
              @click="evh_7172922356531116_click($event, {})"
            >
            </ItpButton>
            <ItpButton
              :disabled="!selectedItems || !selectedItems.length"
              :icon="$config.deleteIcon"
              :text="pgettext('ContractServiceFees', 'Delete')"
              :title="
                pgettext('ContractServiceFees', 'Delete the selected items')
              "
              size="sm"
              variant="light"
              :spinning="actions.bulkDelete.isRunning"
              @click="evh_5246287230194476_click($event, {})"
            >
            </ItpButton>
            <ItpButton
              :disabled="!selectedItems || !selectedItems.length"
              :icon="$config.priceIndexingIcon"
              :text="pgettext('ContractServiceFees', 'Indexing...')"
              size="sm"
              variant="light"
              @click="evh_6939677778299611_click($event, {})"
            >
            </ItpButton>
          </div>
          <PageHelp path="/contracts/contracts.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
